import settings from '@/../settings.json';

const FALLBACK_TOPDOMAIN = 'dev';

/**
 * Interface for how the application settings may be stored
 */
interface IAppSettings {
    ungroupedMappingWorkNo: string;
    fortnoxIntegrationAddress: string;
    fortnoxClientId: string;
    agGridKey: string;
    intercomAppId: string;
    intercomKey: string;
    analyticsKey: string;
    cookiebotId: string;
    enableTranslations: boolean;
    enableGtm: boolean;
    enableVersionCheck: boolean;
    planhatTenantId: string;
    enablePlanhat: boolean;
    satismeterKey: string;
    enableSatismeter: boolean;
    enableOpentelemetry: boolean;
    enableUmami: boolean;
    umamiScriptUrl: string;
    umamiWebsiteId: string;
}

// Load the settings to a variable
export const appSettings: IAppSettings = settings;

const IPV4_PATTERN = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * True when the hostname is an IP address rather than a domain name.
 * IPv6 hostnames are always bracketed in URLs, e.g. `[::1]`.
 */
function isIpAddress(hostname: string): boolean {
    if (IPV4_PATTERN.test(hostname)) return true;
    return hostname.startsWith('[');
}

/**
 * The top domain of the current host, used to build API and docs URLs.
 * Falls back to the dev domain on localhost and when the app is served from an IP address,
 * e.g. when testing a dev build from another machine on the local network.
 */
export function getTopDomain(): string {
    // hostname has no port, unlike host.
    const hostname = window.location.hostname;

    if (hostname === 'localhost') return FALLBACK_TOPDOMAIN;
    if (isIpAddress(hostname)) return FALLBACK_TOPDOMAIN;

    const topDomain = hostname.split('.').pop();

    if (topDomain == null || topDomain === '') return FALLBACK_TOPDOMAIN;
    return topDomain;
}

/**
 * True when API requests should go through the Vite `/api` proxy (see vite.config.mts) so they
 * stay same-origin and never involve CORS. Only applies under the dev server. It is used when:
 * - the app is opened from another machine via IP, since the API's CORS allowlist only covers localhost
 * - `VITE_API_PROXY=true` is set in `.env.local`, e.g. when a VPN interferes with cross-origin requests
 */
function useDevApiProxy(): boolean {
    if (!import.meta.env.DEV) return false;
    if (import.meta.env.VITE_API_PROXY === 'true') return true;
    return isIpAddress(window.location.hostname);
}

/**
 * Host and base path for the API, without protocol.
 */
export function getApiHost(): string {
    if (useDevApiProxy()) {
        return `${window.location.host}/api`;
    }

    return `sysarb.${getTopDomain()}/api`;
}

export function getDocsUrl(): string {
    return `https://docs.sysarb.${getTopDomain()}`;
}
